import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { FileSpreadsheet, FileText } from "lucide-react";

const OPTIONS = [
  { level: 1, title: "Quality name & details", desc: "Image + quality name, shade no., company, WSR" },
  { level: 2, title: "Details + Than stock", desc: "Everything above plus than stock" },
  { level: 3, title: "Details + Than + Meter stock", desc: "Complete sheet with than and meter stock" },
  { level: 4, title: "Details + Meter stock only", desc: "Image + details with meter stock, than stock hidden" },
];

export const ExportDialog = ({ open, onOpenChange, ids }) => {
  const [level, setLevel] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setLevel(1);
  }, [open]);

  const run = async (format) => {
    setBusy(true);
    try {
      const res = await api.post("/export", { ids, level, format }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "pdf" ? "agarwal360-fabric-stock.pdf" : "agarwal360-fabric-stock.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format === "pdf" ? "PDF" : "Excel"} export downloaded`);
      onOpenChange(false);
    } catch (err) {
      let msg = "Export failed";
      if (err.response?.data instanceof Blob) {
        try {
          msg = formatApiErrorDetail(JSON.parse(await err.response.data.text()).detail);
        } catch (_) {}
      } else msg = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="export-dialog" className="rounded-sm sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl font-extrabold tracking-tighter">Export fabrics</DialogTitle>
          <DialogDescription className="sr-only">Choose export detail level and file format</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-zinc-500">
          {ids.length ? `${ids.length} selected item(s)` : "All items in your stock"} · choose what to include
        </p>

        <div className="mt-2 space-y-2">
          {OPTIONS.map((o) => (
            <button
              key={o.level}
              type="button"
              data-testid={`export-option-${o.level}`}
              onClick={() => setLevel(o.level)}
              className={`w-full border p-3 text-left transition-colors duration-200 ${
                level === o.level ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border text-[11px] font-bold ${
                    level === o.level ? "border-zinc-950 bg-[var(--mod-primary)] text-white" : "border-zinc-300"
                  }`}
                >
                  {o.level}
                </span>
                <span>
                  <span className="font-head block text-sm font-bold tracking-tight">{o.title}</span>
                  <span className="block text-xs text-zinc-500">{o.desc}</span>
                </span>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Button
            data-testid="export-pdf-button"
            disabled={busy}
            onClick={() => run("pdf")}
            className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
          >
            <FileText size={15} className="mr-2" /> Export PDF
          </Button>
          <Button
            data-testid="export-excel-button"
            disabled={busy}
            variant="outline"
            onClick={() => run("excel")}
            className="rounded-sm border-zinc-950"
          >
            <FileSpreadsheet size={15} className="mr-2" /> Export Excel
          </Button>
        </div>
        {busy && <p className="mt-2 text-center text-xs text-zinc-500">Generating file…</p>}
      </DialogContent>
    </Dialog>
  );
};
